import { DomPortalOutlet, PortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  Inject,
  Injector,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { consumerPollProducersForChange } from '@angular/core/primitives/signals';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BaseApiService,
  basePath,
  CardsApiService,
  filePath,
  geteCardTypeByClassName,
  PersonApiService,
  SourceArcApiService,
  SourceBibApiService
} from '@atmi/core';
import { ExtendedSourceBib } from '@atmi/core/models/extended-source-bib';

import { CollectionOrderSettings } from '@atmi/core/services/settings-api.service';
import { CardOAJoined } from '@atmi/models/cards/card-oa';
import {
  beneOaSwitchesDefaultValues,
  BeneOA_Path_NiceNames,
  BeneOA_Path_NiceNames_B,
  cardOAtoPrintTransformers, showRoomPropPrintOrder, sortByOrder
} from '@sim/collections/dialogs/collection-export-dialog/constants';
import { groupByX, waitT } from '@sim/collections/dialogs/collection-export-dialog/routines';
import {
  CardOaPrintCheck,
  CardOAPrintTransformer,
  JoinableProps
} from '@sim/collections/dialogs/collection-export-dialog/types';
import { CollectionItem } from '@sim/collections/models/collection';
import { CollectionItemApiService } from '@sim/collections/services';
import { MediaService } from '@sim/media';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';


/*
* arrivati qui dobbiamo gia avere tutti i dati escluse le immagini
* le immagini saranno sotto forma di url nei dati e verranno caricate una volta che l'utente clicca preview
* https://dev.to/fasidongit/the-near-ultimate-guide-to-printing-anything-using-angular-5h4j
* */
@Component({
  selector: 'app-collection-export-dialog',
  templateUrl: './collection-export-dialog.component.html',
  styleUrls: ['./collection-export-dialog.component.scss']
})
export class CollectionExportDialogComponent implements OnInit {
  firstFormGroup!: UntypedFormGroup;
  secondFormGroup!: UntypedFormGroup;

  showPreview = false;

  loading = false;

  printListForShowRoom: Array<any[]> = [];
  printListForArchive: Array<{ obj: CardOAJoined, printArray: string[][], previewUrl: string }> = [];

  /*how many items on each a4 page for model showroom*/
  itemsPerPage = 3;
  secondaryItemsPerPage = 4;
  secondaryItemsPerPageEven = 4;
  secondaryItemEvenHeight = `${(100 / this.secondaryItemsPerPageEven).toFixed(2)}%`;
  secondaryItemHeight = `${(100 / this.secondaryItemsPerPage).toFixed(2)}%`;

  selectedModel: 'model_showroom' | 'model_archive' = 'model_showroom';

  readonly BeneOA_Path_NiceNames = BeneOA_Path_NiceNames;
  readonly namesOfFirstStep = Object.keys(BeneOA_Path_NiceNames);
  readonly BeneOA_Path_NiceNames_B = BeneOA_Path_NiceNames_B;
  readonly namesOfSecondStep = Object.keys(BeneOA_Path_NiceNames_B);

  secondToPrintMap: Record<string, boolean> = {};
  // print stuff
  // @ts-ignore
  private portalHost: PortalOutlet;
  @ViewChild('printme') printRef: any; // printable content.
  @ViewChild('iframe') iframe: any; // target host to render the printable content

  /*@TODO add 2 sub component to print showroom and archive templates*/
  collectionItems: CollectionItem[] = [];

  constructor(
    @Inject(basePath) private readonly baseUrl: string,
    private sanitizer: DomSanitizer,
    private readonly collectionItemApi: CollectionItemApiService,
    private readonly cardApiService: CardsApiService,
    private readonly sourceArcApi: SourceArcApiService,
    private readonly sourceBibApi: SourceBibApiService,
    private readonly personApi: PersonApiService,
    private readonly baseApi: BaseApiService,
    private readonly mediaApi: MediaService,
    private readonly spinnerService: NgxSpinnerService,
    private snackBar: MatSnackBar,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    private viewContainerRef: ViewContainerRef,
    private dialogRef: MatDialogRef<CollectionExportDialogComponent>,
    @Inject(filePath) readonly fileUrl: string,
    @Inject(MAT_DIALOG_DATA)
    public data: { collectionId: string, collectionOrderSettings: CollectionOrderSettings },
  ) {
  }

  ngOnInit() {
    this.firstFormGroup = this.buildSwitchFormGroup(this.BeneOA_Path_NiceNames);
    this.secondFormGroup = this.buildSwitchFormGroup(this.BeneOA_Path_NiceNames_B);
  }

  ngAfterViewInit(): void {
    this.getCollectionItems();
  }


  async getCollectionItems() {
    this.loading = true;
    this.spinnerService.show('loading-objects');

    try {
      const itemsRes = await firstValueFrom(this.collectionItemApi.index(this.data.collectionId).pipe(first()));

      const currentCollectionOrderSettings = this.data.collectionOrderSettings?.[this.data.collectionId] || null;
      if (!currentCollectionOrderSettings) {
        this.collectionItems = itemsRes.data;
        return;
      }

      this.collectionItems = itemsRes.data.sort((a, b) => {
        const aIndex = currentCollectionOrderSettings.indexOf(a.objectId) || 0;
        const bIndex = currentCollectionOrderSettings.indexOf(b.objectId) || 0;
        return aIndex - bIndex;
      });


    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
      this.spinnerService.hide('loading-objects');
    }
  }


  ngOnDestroy(): void {
    if (this.portalHost && this.portalHost.detach) {
      this.portalHost.detach();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  async printMainContent(): Promise<boolean> {

    this.showPreview = false;

    this.spinnerService.show('loading-iframe');
    this.loading = true;

    const iframe = this.iframe.nativeElement;
    this.portalHost = new DomPortalOutlet(
      iframe.contentDocument.body,
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );

    this._attachStyles(iframe.contentWindow);

    const portal = new TemplatePortal(
      this.printRef,
      this.viewContainerRef,
    );


    // Attach portal to host
    this.portalHost.attach(portal);

    iframe.contentWindow.onafterprint = () => {
      iframe.contentDocument.body.innerHTML = '';
    };

    try {
      await this.waitForImageToLoad(
        iframe,
      );
      await waitT(500);
    } catch (err) {
      console.error(err);
    } finally {

      setTimeout(async () => {
        this.iframe.nativeElement.contentWindow.print();
        await waitT(500);
        this.spinnerService.hide('loading-iframe');
        this.loading = false;
      }, 200);
    }

    return true;
  }

  async preloadImages(imagesUri: string[]) {
    return Promise.all(
      Array.from(imagesUri).map((imgUri: string, _) => {
        return new Promise((resolve, _) => {
          // https://stackoverflow.com/questions/76719958/how-to-deal-with-browser-cache-in-angular
          const img = new Image();
          img.src = `${this.fileUrl}${imgUri}`;
          img.onload = () => {
            resolve(true);
          };
          img.onerror = (err) => {
            console.error({ imgUri, err });
            resolve(true);
          };
        });
      })
    );
  }

  private async waitForImageToLoad(iframe: HTMLIFrameElement): Promise<Array<any>> {

    const allImages = iframe.contentDocument?.body.querySelectorAll(
      'div.img-container'
    ) as unknown as HTMLImageElement[];

    return Promise.all(
      Array.from(allImages).map((divImgContainer: HTMLElement, _) => {
        return new Promise((resolve, _) => {
          const uri = divImgContainer.style.backgroundImage
            .replace('url("', '')
            .replace('")', '')
            .replace('data:', '')
            .replace(/^.+,/, '');

          if (uri.indexOf('base64') !== -1) {
            return resolve(true);
          }

          const img = new Image();
          img.src = uri;
          img.onload = () => {
            resolve(true);
          };
          img.onerror = (err) => {
            console.error({ uri, err });
            resolve(true);
          };
        });
      })
    );

  }


  private _attachStyles(targetWindow: Window): void {
    // Copy styles from parent window
    document.querySelectorAll('style').forEach(htmlElement => {
      targetWindow.document.head.appendChild(htmlElement.cloneNode(true));
    });
    // Copy stylesheet link from parent window
    const styleSheetElement = this._getStyleSheetElement();
    targetWindow.document.head.appendChild(styleSheetElement);
  }

  private _getStyleSheetElement() {
    const styleSheetElement = document.createElement('link');
    document.querySelectorAll('link').forEach(htmlElement => {
      if (htmlElement.rel === 'stylesheet') {
        const absoluteUrl = new URL(htmlElement.href).href;
        styleSheetElement.rel = 'stylesheet';
        styleSheetElement.type = 'text/css';
        styleSheetElement.href = absoluteUrl;
      }
    });

    return styleSheetElement;
  }

  buildSwitchFormGroup = (config: Record<string, string>): UntypedFormGroup => {
    const fg: Record<string, UntypedFormControl> = {};
    Object.entries(config).forEach(([key, _]) => {
      const initValue = beneOaSwitchesDefaultValues[key as keyof CardOaPrintCheck] || false;
      fg[key] = new UntypedFormControl(initValue);
    });
    return new UntypedFormGroup(fg);
  }

  getSliderNameFirstStep = (name: string): string => this.BeneOA_Path_NiceNames[name] || name;
  getSliderNameSecondStep = (name: string): string => this.BeneOA_Path_NiceNames_B[name] || name;

  euroToPrint(entry: string[]): boolean {
    return ['Valore Assicurativo', 'Prezzo Pub.Imp.'].includes(entry[0]);
  }


  /*pass collection object from parent table, already in sorted order!!!
  * remove logic from this component*/
  async getCollectionObjects(joinablePropToGet: JoinableProps): Promise<CardOAJoined[] | null> {

    const allImagesUris: string[] = [];

    if (this.data.collectionId) {
      try {

        const obj = { data: this.collectionItems };


        const collectionObjects = await Promise.all(obj.data.map(async (oggetto) => {
          const cardType = geteCardTypeByClassName(oggetto.className);
          const objectData = await firstValueFrom(this.cardApiService.get(oggetto.objectId, cardType).pipe(first()));

          // Derive previewUrl from card's attachments, not from collection item
          let cardPreviewUrl = '';
          if (objectData.allegati && objectData.allegati.length > 0) {
            const firstImageAttachment = objectData.allegati.find((allegato: any) => allegato.tipo === 'immagine') || objectData.allegati[0];
            if (firstImageAttachment && firstImageAttachment.file) {
              try {
                const media = await firstValueFrom(this.mediaApi.get(firstImageAttachment.file).pipe(first()));
                cardPreviewUrl = media.previewUrl || '';
              } catch (err) {
                console.error('Error fetching media for preview:', err);
              }
            }
          }

          // Fallback to collection item previewUrl if no card attachments found
          const finalPreviewUrl = cardPreviewUrl || oggetto.previewUrl?.replace('180x180', '512x512') || oggetto.previewUrl;

          if (finalPreviewUrl) {
            allImagesUris.push(finalPreviewUrl);
          }

          const { fornitore, fontiBib, fontiArc, base, benePadre, ...restObj } = objectData;

          const fullObjectData: CardOAJoined = { ...restObj, previewUrl: finalPreviewUrl } as CardOAJoined;

          if (benePadre) {
            const parentData = await firstValueFrom(this.cardApiService.get(benePadre, cardType).pipe(first()));
            fullObjectData.benePadre = parentData;
          } else {
            fullObjectData.benePadre = null;
          }

          if (base?.length) {
            fullObjectData.base = await firstValueFrom(this.baseApi.get(base).pipe(first()));
          }

          if (joinablePropToGet.fornitore && fornitore) {
            fullObjectData.fornitore = await firstValueFrom(this.personApi.get(fornitore).pipe(first()));
          }

          /*fonti bibliografiche*/
          if (joinablePropToGet.fontiBib && fontiBib.length) {
            // Get the SourceBibliographic objects
            const sourceBibliographics = await this.sourceBibApi.getMany(fontiBib.sort(sortByOrder).map(fonte => fonte.docBib));

            // Create ExtendedSourceBib objects by combining SourceBibliographic with riferimento from fontiBib
            fullObjectData.fontiBib = sourceBibliographics.map((sourceBib) => {
              const matchingFontiBib = fontiBib.find(fonte => fonte.docBib === sourceBib.id);
              return {
                ...sourceBib,
                riferimento: matchingFontiBib?.riferimento || '',
                ordine: matchingFontiBib?.ordine
              } as ExtendedSourceBib;
            });

            // Sort fontiBib by codiceUnico
            fullObjectData.fontiBib.sort((a, b) => {
              if (a.codiceUnico && b.codiceUnico) {
                return a.codiceUnico.localeCompare(b.codiceUnico);
              }
              return 0;
            });

            await Promise.all(fullObjectData.fontiBib.map(async (fonte) => {
              if (!fonte.immagine) {
                return fonte;
              }
              let media = null;
              try {
                media = await firstValueFrom(this.mediaApi.get(fonte.immagine).pipe(first()));
                // if no preview use full image
                fonte.immagine = await this.mediaApi.getBase64FromUrl(`${this.fileUrl}${media.previewUrl}`);
                // await this.mediaApi.getBase64Image(media.id || '');

              } catch (err) {
                fonte.immagine = `${this.fileUrl}${media?.previewUrl || fonte.immagine}`;
                console.error({ err, img: fonte.immagine, media, mediaApi: this.mediaApi });
                return fonte;
              }

              // allImagesUris.push(fonte.immagine);

              return fonte;
            }));
            fullObjectData.fontiBibGrouped = groupByX(fullObjectData.fontiBib, this.secondaryItemsPerPageEven);
          }

          /*fonti archivistiche*/
          if (joinablePropToGet.fontiArc && fontiArc.length) {
            fullObjectData.fontiArc = await this.sourceArcApi.getMany(fontiArc.sort(sortByOrder).map(fonte => fonte.docArc));

            await Promise.all(fullObjectData.fontiArc.map(async (fonte) => {
              if (!fonte.immagine) {
                return fonte;
              }
              let media = null;
              try {
                media = await firstValueFrom(this.mediaApi.get(fonte.immagine).pipe(first()));
                fonte.immagine = await this.mediaApi.getBase64FromUrl(`${this.fileUrl}${media.previewUrl}`);
              } catch (err) {
                fonte.immagine = `${this.fileUrl}${media?.previewUrl || fonte.immagine}`;
                console.error({ err, img: fonte.immagine, media, mediaApi: this.mediaApi });
                return fonte;
              }

              // allImagesUris.push(fonte.immagine);

              return fonte;
            }));
            fullObjectData.fontiArcGrouped = groupByX(fullObjectData.fontiArc, this.secondaryItemsPerPageEven);
          }

          /*allegati*/
          if (joinablePropToGet.allegati && objectData.allegati.length) {
            fullObjectData.attachments = await this.mediaApi.getMany(fullObjectData.allegati.map(allegato => allegato.file));
            fullObjectData.attachmentsGrouped = groupByX(fullObjectData.attachments, this.secondaryItemsPerPage);
          }

          /*movimenti*/
          if (objectData.movimenti?.length) {
            fullObjectData.movimentiGrouped = groupByX(fullObjectData.movimenti, this.secondaryItemsPerPageEven);
          }

          /*iscrizioni*/
          if (objectData.ISR?.length) {
            fullObjectData.iscrizioniGrouped = groupByX(fullObjectData.ISR, this.secondaryItemsPerPageEven);
          }

          return fullObjectData;
        }));

        await this.preloadImages(allImagesUris);
        return collectionObjects;

      } catch (err) {
        console.error(err);
      }
    }

    return null;
  }

  closePreview() {
    this.showPreview = false;
  }

  loadAndPrintMainContent = async () => {
    try {
      await this.handlePrintClick(false);
      await this.printMainContent();
    } catch (err) {
      console.error(err);
    }

  }


  handlePrintClick = async (show: boolean = true) => {

    const firstToPrint = Object.entries(this.firstFormGroup.value)
      .filter(([_, v]) => Boolean(v))
      .map(([k, _]) => k) as Array<keyof CardOaPrintCheck>;

    const secondToPrint = Object.entries(this.secondFormGroup.value)
      .filter(([_, v]) => Boolean(v))
      .map(([k, _]) => k);

    secondToPrint.forEach(prop => this.secondToPrintMap[prop] = true);

    this.spinnerService.show('loading-objects');
    this.loading = true;

    try {

      const objectsData = await this.getCollectionObjects({
        fornitore: this.secondToPrintMap['fornitore'],
        fontiArc: this.secondToPrintMap['fontiArc'],
        fontiBib: this.secondToPrintMap['fontiBib'],
        allegati: this.secondToPrintMap['allegati']
      });

      if (!objectsData) {
        return;
      }// @TODO notify missing objects in collection


      const toPrint = objectsData.map(obj => {
        const sortedToPrint = firstToPrint.sort((a, b) => {
          return showRoomPropPrintOrder[a] > showRoomPropPrintOrder[b] ? 1 : -1;
        });

        const printArray = sortedToPrint
          .map(prop => [prop, cardOAtoPrintTransformers[prop]])
          .filter((values): values is [keyof CardOaPrintCheck, CardOAPrintTransformer] => Boolean(values[0] && values[1]))
          .map(([prop, fnTransform]) =>
            [prop, fnTransform(obj)])
          .map(([prop, value]) => [BeneOA_Path_NiceNames[prop], value])
          .filter(([_, value]) => {
            return value !== null && value !== undefined && value !== '' && value !== 'undefined' && value !== 'null';
          });


        return { obj, printArray, previewUrl: `${this.fileUrl}${obj.previewUrl}` };
      });

      this.printListForShowRoom = groupByX(toPrint, this.itemsPerPage);

      /*setup model archive*/
      this.printListForArchive = toPrint;

      this.showPreview = show;

    } catch (err) {
      console.error(err);
      this.snackBar.open('Error loading data to print');
      this.dialogRef.close();
    } finally {
      this.loading = false;
      this.spinnerService.hide('loading-objects');
    }

  }
}



